import { create } from 'zustand';
import { VariationsDetailGridMetricType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';

interface VariationsFilterState {
  isOpen: boolean;
  isSingleSkuPagesEnabled: boolean;
  isVariatedSkuPagesEnabled: boolean;
  isCorrectChildSkusEnabled: boolean;
  isIncorrectChildSkusEnabled: boolean;
  isInaccurateParentPagesEnabled: boolean;
  tempIsSingleSkuPagesEnabled: boolean;
  tempIsVariatedSkuPagesEnabled: boolean;
  tempIsCorrectChildSkusEnabled: boolean;
  tempIsIncorrectChildSkusEnabled: boolean;
  tempIsInaccurateParentPagesEnabled: boolean;
  dropdownOption: VariationsDetailGridMetricType;
  openModal: () => void;
  closeModal: () => void;
  setIsSingleSkuPagesEnabled: (value: boolean) => void;
  setIsVariatedSkuPagesEnabled: (value: boolean) => void;
  setIsCorrectChildSkusEnabled: (value: boolean) => void;
  setIsIncorrectChildSkusEnabled: (value: boolean) => void;
  setIsInaccurateParentPagesEnabled: (value: boolean) => void;
  setTempSingleSkuPages: (value: boolean) => void;
  setTempVariatedSkuPages: (value: boolean) => void;
  setTempCorrectChildSkus: (value: boolean) => void;
  setTempIncorrectChildSkus: (value: boolean) => void;
  setTempInaccurateParentPages: (value: boolean) => void;
  setDropdownOption: (option: VariationsDetailGridMetricType) => void;
  resetFilters: () => void;
  applyFilters: () => void;
  getActiveFilter: () => string[];
  getFilterOptions: (dropdownOption: VariationsDetailGridMetricType) => Array<{
    id: string;
    label: string;
    isChecked: boolean;
    setTempFilter: (value: boolean) => void;
  }>;
}

const FilterNames = {
  singleSkuPages: 'single SKU pages',
  variatedSkuPages: 'variated SKU pages',
  correctChildSkus: 'correct child SKUs',
  incorrectChildSkus: 'incorrect child SKUs',
  inaccurateParentPages: 'inaccurate parent pages'
};

const defaultFilterState = {
  isSingleSkuPagesEnabled: false,
  isVariatedSkuPagesEnabled: false,
  isCorrectChildSkusEnabled: false,
  isIncorrectChildSkusEnabled: false,
  isInaccurateParentPagesEnabled: false,
  tempIsSingleSkuPagesEnabled: false,
  tempIsVariatedSkuPagesEnabled: false,
  tempIsCorrectChildSkusEnabled: false,
  tempIsIncorrectChildSkusEnabled: false,
  tempIsInaccurateParentPagesEnabled: false
};

export const useVariationsFilterStore = create<VariationsFilterState>((set, get) => ({
  isOpen: false,
  ...defaultFilterState,
  dropdownOption: VariationsDetailGridMetricType.PageDetailGrid,

  openModal: () =>
    set((state) => ({
      isOpen: true,
      tempIsSingleSkuPagesEnabled: state.isSingleSkuPagesEnabled,
      tempIsVariatedSkuPagesEnabled: state.isVariatedSkuPagesEnabled,
      tempIsCorrectChildSkusEnabled: state.isCorrectChildSkusEnabled,
      tempIsIncorrectChildSkusEnabled: state.isIncorrectChildSkusEnabled,
      tempIsInaccurateParentPagesEnabled: state.isInaccurateParentPagesEnabled
    })),
  closeModal: () => set({ isOpen: false }),
  setIsSingleSkuPagesEnabled: (value: boolean) =>
    set({
      ...defaultFilterState,
      isSingleSkuPagesEnabled: value,
      tempIsSingleSkuPagesEnabled: value,
      dropdownOption: VariationsDetailGridMetricType.ChildDetailGrid
    }),
  setIsVariatedSkuPagesEnabled: (value: boolean) =>
    set({
      ...defaultFilterState,
      isVariatedSkuPagesEnabled: value,
      tempIsVariatedSkuPagesEnabled: value,
      dropdownOption: VariationsDetailGridMetricType.ChildDetailGrid
    }),
  setIsCorrectChildSkusEnabled: (value: boolean) =>
    set({
      ...defaultFilterState,
      isCorrectChildSkusEnabled: value,
      tempIsCorrectChildSkusEnabled: value,
      dropdownOption: VariationsDetailGridMetricType.ChildDetailGrid
    }),
  setIsIncorrectChildSkusEnabled: (value: boolean) =>
    set({
      ...defaultFilterState,
      isIncorrectChildSkusEnabled: value,
      tempIsIncorrectChildSkusEnabled: value,
      dropdownOption: VariationsDetailGridMetricType.ChildDetailGrid
    }),
  setIsInaccurateParentPagesEnabled: (value: boolean) =>
    set({
      ...defaultFilterState,
      isInaccurateParentPagesEnabled: value,
      tempIsInaccurateParentPagesEnabled: value,
      dropdownOption: VariationsDetailGridMetricType.PageDetailGrid
    }),
  setTempSingleSkuPages: (value: boolean) =>
    set((state) => ({
      tempIsSingleSkuPagesEnabled: value,
      tempIsVariatedSkuPagesEnabled: value ? false : state.tempIsVariatedSkuPagesEnabled,
      tempIsCorrectChildSkusEnabled: value ? false : state.tempIsCorrectChildSkusEnabled,
      tempIsIncorrectChildSkusEnabled: value ? false : state.tempIsIncorrectChildSkusEnabled
    })),
  setTempVariatedSkuPages: (value: boolean) =>
    set((state) => ({
      tempIsVariatedSkuPagesEnabled: value,
      tempIsSingleSkuPagesEnabled: value ? false : state.tempIsSingleSkuPagesEnabled,
      tempIsCorrectChildSkusEnabled: value ? false : state.tempIsCorrectChildSkusEnabled,
      tempIsIncorrectChildSkusEnabled: value ? false : state.tempIsIncorrectChildSkusEnabled
    })),
  setTempCorrectChildSkus: (value: boolean) =>
    set((state) => ({
      tempIsCorrectChildSkusEnabled: value,
      tempIsSingleSkuPagesEnabled: value ? false : state.tempIsSingleSkuPagesEnabled,
      tempIsVariatedSkuPagesEnabled: value ? false : state.tempIsVariatedSkuPagesEnabled,
      tempIsIncorrectChildSkusEnabled: value ? false : state.tempIsIncorrectChildSkusEnabled
    })),
  setTempIncorrectChildSkus: (value: boolean) =>
    set((state) => ({
      tempIsIncorrectChildSkusEnabled: value,
      tempIsSingleSkuPagesEnabled: value ? false : state.tempIsSingleSkuPagesEnabled,
      tempIsVariatedSkuPagesEnabled: value ? false : state.tempIsVariatedSkuPagesEnabled,
      tempIsCorrectChildSkusEnabled: value ? false : state.tempIsCorrectChildSkusEnabled,
      tempIsInaccurateParentPagesEnabled: value ? false : state.tempIsInaccurateParentPagesEnabled
    })),
  setTempInaccurateParentPages: (value: boolean) =>
    set((state) => ({
      tempIsInaccurateParentPagesEnabled: value,
      tempIsSingleSkuPagesEnabled: value ? false : state.tempIsSingleSkuPagesEnabled,
      tempIsVariatedSkuPagesEnabled: value ? false : state.tempIsVariatedSkuPagesEnabled,
      tempIsCorrectChildSkusEnabled: value ? false : state.tempIsCorrectChildSkusEnabled,
      tempIsIncorrectChildSkusEnabled: value ? false : state.tempIsIncorrectChildSkusEnabled
    })),

  setDropdownOption: (option: VariationsDetailGridMetricType) => set({ dropdownOption: option }),

  resetFilters: () =>
    set({
      ...defaultFilterState
    }),

  applyFilters: () =>
    set((state) => ({
      isSingleSkuPagesEnabled: state.tempIsSingleSkuPagesEnabled,
      isVariatedSkuPagesEnabled: state.tempIsVariatedSkuPagesEnabled,
      isCorrectChildSkusEnabled: state.tempIsCorrectChildSkusEnabled,
      isIncorrectChildSkusEnabled: state.tempIsIncorrectChildSkusEnabled,
      isInaccurateParentPagesEnabled: state.tempIsInaccurateParentPagesEnabled,
      isOpen: false
    })),

  getActiveFilter: () => {
    const filters: string[] = [];
    if (get().isSingleSkuPagesEnabled) filters.push(FilterNames.singleSkuPages);
    if (get().isVariatedSkuPagesEnabled) filters.push(FilterNames.variatedSkuPages);
    if (get().isCorrectChildSkusEnabled) filters.push(FilterNames.correctChildSkus);
    if (get().isIncorrectChildSkusEnabled) filters.push(FilterNames.incorrectChildSkus);
    if (get().isInaccurateParentPagesEnabled) filters.push(FilterNames.inaccurateParentPages);
    return filters;
  },

  getFilterOptions: (dropdownOption: VariationsDetailGridMetricType) => {
    const state = get();
    const options = [];

    if (dropdownOption === VariationsDetailGridMetricType.PageDetailGrid) {
      options.push({
        id: 'inaccurateParentPages',
        label: 'Inaccurate Parent Pages',
        isChecked: state.tempIsInaccurateParentPagesEnabled,
        setTempFilter: state.setTempInaccurateParentPages
      });
    } else if (dropdownOption === VariationsDetailGridMetricType.ChildDetailGrid) {
      options.push(
        {
          id: 'singleSkuPages',
          label: 'Single SKU Pages',
          isChecked: state.tempIsSingleSkuPagesEnabled,
          setTempFilter: state.setTempSingleSkuPages
        },
        {
          id: 'variatedSkuPages',
          label: 'Variated SKU Pages',
          isChecked: state.tempIsVariatedSkuPagesEnabled,
          setTempFilter: state.setTempVariatedSkuPages
        },
        {
          id: 'correctChildSkus',
          label: 'Correct Child SKUs',
          isChecked: state.tempIsCorrectChildSkusEnabled,
          setTempFilter: state.setTempCorrectChildSkus
        },
        {
          id: 'incorrectChildSkus',
          label: 'Incorrect Child SKUs',
          isChecked: state.tempIsIncorrectChildSkusEnabled,
          setTempFilter: state.setTempIncorrectChildSkus
        }
      );
    } else {
      throw new Error(`Invalid dropdown option: ${dropdownOption}`);
    }

    return options;
  }
}));
